// components/ConfirmationModal.tsx

import React from 'react';
import { Modal, Text, Group, Button, Stack, ThemeIcon, Box } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconAlertCircle } from '@tabler/icons-react';

export interface ConfirmationModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type?: 'info' | 'warning' | 'danger';
    loading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    opened,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'warning',
    loading = false,
}) => {
    const getIcon = () => {
        switch (type) {
            case 'danger':
                return <IconAlertCircle size={24} />;
            case 'warning':
                return <IconAlertTriangle size={24} />;
            default:
                return <IconInfoCircle size={24} />;
        }
    };

    const getColor = () => {
        switch (type) {
            case 'danger':
                return 'red';
            case 'warning':
                return 'yellow';
            default:
                return 'blue';
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="sm">
                    <ThemeIcon color={getColor()} variant="light" size="lg">
                        {getIcon()}
                    </ThemeIcon>
                    <Text fw={600} size="lg">{title}</Text>
                </Group>
            }
            centered
            size="sm"
            closeOnClickOutside={!loading}
            closeOnEscape={!loading}
            withCloseButton={!loading}
        >
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    {message}
                </Text>

                <Group justify="flex-end" gap="sm">
                    <Button
                        variant="default"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelText}
                    </Button>
                    <Button
                        color={getColor()}
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                        loading={loading}
                    >
                        {confirmText}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

// Hook for managing confirmation state
export const useConfirmation = () => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [config, setConfig] = React.useState<Omit<ConfirmationModalProps, 'opened' | 'onClose' | 'onConfirm'>>({
        title: '',
        message: '',
    });
    const [resolver, setResolver] = React.useState<((value: boolean) => void) | null>(null);

    const confirm = React.useCallback((
        options: Omit<ConfirmationModalProps, 'opened' | 'onClose' | 'onConfirm'>
    ): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfig(options);
            setIsOpen(true);
            setResolver(() => resolve);
        });
    }, []);

    const handleConfirm = React.useCallback(() => {
        resolver?.(true);
        setIsOpen(false);
        setResolver(null);
    }, [resolver]);

    const handleCancel = React.useCallback(() => {
        resolver?.(false);
        setIsOpen(false);
        setResolver(null);
    }, [resolver]);

    return {
        confirm,
        ConfirmationModal: (
            <ConfirmationModal
                opened={isOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                {...config}
            />
        ),
    };
};